<template>
  <div class="user-orders-container">
    <!-- 搜索区域 - 简洁透明版 -->
    <div class="search-area">
      <el-row :gutter="20" align="middle">
        <el-col :span="18">
          <el-space>
            <el-input
                v-model="searchForm.keyword"
                placeholder="搜索订单编号、插件名称"
                style="width: 300px"
                clearable
                :prefix-icon="Search"
                @clear="clearSearch"
                @keyup.enter="handleSearch"
            />
            <el-button type="primary" @click="handleSearch">搜索</el-button>
          </el-space>
        </el-col>
      </el-row>
    </div>

    <!-- 订单表格区域 - 带水平 + 垂直滚动 -->
    <div class="table-wrapper">
      <el-table
          :data="tableData"
          v-loading="loading"
          class="order-table"
          stripe
          border
          max-height="560"
          empty-text="暂无相关信息"
      >
        <el-table-column prop="orderNo" label="订单编号" min-width="150" align="center" />
        <el-table-column prop="productCode" label="插件编码" min-width="120" align="center" />
        <el-table-column prop="productName" label="插件名称" min-width="140" align="center" />
        <el-table-column prop="totalFee" label="金额（元）" min-width="110" align="center">
          <template #default="scope">
            <span class="order-amount">¥{{ scope.row.totalFee?.toFixed(2) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="purchasedDays" label="时长(天)" min-width="100" align="center" />
        <el-table-column prop="paidAt" label="购买时间" min-width="120" align="center">
          <template #default="scope">
            {{ scope.row.paidAt ? formatDateTime(scope.row.paidAt) : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="paymentMethodName" label="支付方式" min-width="100" align="center" />
        <el-table-column prop="paymentStatus" label="状态" min-width="110" align="center">
          <template #default="scope">
            <el-tag :type="getStatusTagType(scope.row.paymentStatus)" class="status-tag">
              {{ formatStatus(scope.row.paymentStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" min-width="130" align="center" fixed="right">
          <template #default="{ row }">
            <template v-if="row.isAip && row.paymentStatus === 1">
              <el-button
                  v-if="row.activationStatus !== 1"
                  type="success" size="small"
                  @click="openActivateDialog(row)"
                  class="action-btn activate-btn"
              >激活</el-button>
              <el-button
                  v-else
                  type="info" size="small"
                  @click="openViewCodeDialog(row)"
                  class="action-btn view-btn"
              >查看激活码</el-button>
            </template>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 分页组件 -->
    <div class="pagination-container">
      <el-pagination
          v-model:current-page="currentPage"
          :page-size="pageSize"
          :total="total"
          :pager-count="5"
          layout="prev, pager, next"
          @current-change="handleCurrentChange"
      />
    </div>

    <!-- 激活码弹窗 -->
    <el-dialog
        v-model="showActivateDialog"
        @closed="resetActivationState"
        :width="activateCodeResult ? '620px' : '420px'"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        class="activation-dialog"
        :show-close="false"
        append-to-body
        modal-class="activation-dialog-overlay"
    >
      <template #header>
        <div class="dialog-header-custom">
          <span></span>
          <button class="custom-close-btn" @click="showActivateDialog = false">✕</button>
        </div>
      </template>

      <div class="activation-content">
        <template v-if="!activateCodeResult">
          <div class="activation-title">
            <svg class="key-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="7.5" cy="15.5" r="4.5"/>
              <path d="m10.7 12.3 8.8-8.8M17 6l2 2M20 3l1 1"/>
            </svg>
            <span>{{ currentActivateRow?.productName || '插件' }} - 激活码生成器</span>
          </div>

          <div class="form-group">
            <div class="input-label-row">
              <label class="input-label">客户机器码</label>
            </div>
            <el-input
                v-model="activateDeviceFingerprint"
                placeholder="XXXX-XXXX-XXXX-XXXX"
                class="fingerprint-input"
                :spellcheck="false"
                @input="clearActivateError"
            />
            <div v-if="activateDeviceFingerprint.trim() && isActivateFingerprintValid === false" class="format-hint error">
              <el-icon><WarningFilled /></el-icon>
              <span>机器码格式不正确</span>
            </div>
            <div v-else-if="activateShowSuccess && activateDeviceFingerprint.trim() && isActivateFingerprintValid === true" class="format-hint success">
              <el-icon><SuccessFilled /></el-icon>
              <span>机器码格式正确</span>
            </div>
          </div>

          <div class="validity-row">
            <div class="validity-item">
              <div class="validity-icon">
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="9"/>
                  <path d="M12 7v5l3.5 2"/>
                </svg>
              </div>
              <div class="validity-text">
                <span class="label">有效期</span>
                <span class="value">{{ currentActivateRow?.purchasedDays ? currentActivateRow.purchasedDays + ' 天' : '按订单天数' }}</span>
              </div>
            </div>
            <div class="validity-divider"></div>
            <div class="validity-item">
              <div class="validity-icon">
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="5" width="18" height="16" rx="2"/>
                  <path d="M16 3v4M8 3v4M3 10h18"/>
                </svg>
              </div>
              <div class="validity-text">
                <span class="label">到期日期</span>
                <span class="value">{{ calcExpireDate(currentActivateRow) }}</span>
              </div>
            </div>
          </div>

          <el-button
              type="primary"
              :loading="activating"
              @click="confirmActivate"
              :disabled="!activateDeviceFingerprint.trim() || isActivateFingerprintValid === false"
              class="generate-btn"
          >
            <el-icon v-if="!activating"><MagicStick /></el-icon>
            <span>{{ activating ? '正在生成...' : '生成激活码' }}</span>
          </el-button>

          <el-alert
              v-if="activateErrorMsg"
              :title="activateErrorMsg"
              type="error"
              show-icon
              :closable="false"
              class="license-error-alert"
          />
        </template>

        <template v-else>
          <div class="activation-result-full">
            <div class="result-header">
              <span class="result-icon">✅</span>
              <!-- 标题添加 title-dark 类，使用纯黑色 -->
              <h3 class="result-title title-dark">激活码已生成</h3>
            </div>
            <!-- 展示区域添加 default-scroll 类，恢复默认滚动条 -->
            <div class="code-display default-scroll">{{ activateCodeResult }}</div>
            <el-button class="copy-btn" @click="copyActivateCode">
              <el-icon><CopyDocument /></el-icon> 复制激活码
            </el-button>
            <p class="hint">请妥善保存，复制后关闭窗口</p>
          </div>
        </template>

        <div class="footer-info">
          {{ currentActivateRow?.productName || '插件' }} · 激活算法 V1
        </div>
      </div>
    </el-dialog>

    <!-- 查看激活码弹窗 -->
    <el-dialog
        v-model="showViewCodeDialog"
        width="560px"
        title="查看激活码"
        class="professional-code-dialog"
        append-to-body
    >
      <div class="code-dialog-body">
        <p class="code-dialog-label">激活码</p>
        <div class="code-dialog-value">{{ viewCode }}</div>
        <div class="code-dialog-actions">
          <el-button type="primary" @click="copyViewCode">
            <el-icon><CopyDocument /></el-icon>
            复制激活码
          </el-button>
          <el-button @click="showViewCodeDialog = false">关闭</el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive, computed  } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search, WarningFilled, SuccessFilled, MagicStick, CopyDocument } from '@element-plus/icons-vue'
import request from '@/utils/request'

const loading = ref(false)
const tableData = ref([])
const currentPage = ref(1)
const pageSize = ref(8)
const total = ref(0)

const searchForm = reactive({
  keyword: ''
})

const showActivateDialog = ref(false)
const currentActivateRow = ref(null)
const activateDeviceFingerprint = ref('')
const activating = ref(false)
const activateCodeResult = ref('')
const activateErrorMsg = ref('')
const activateShowSuccess = ref(true)

const showViewCodeDialog = ref(false)
const viewCode = ref('')

const calcExpireDate = (row) => {
  const days = Number(row?.purchasedDays || 0)
  if (!days) return '-'
  const date = new Date()
  date.setDate(date.getDate() + days)
  return date.toISOString().slice(0, 10)
}

const isActivateFingerprintValid = computed(() => {
  const value = activateDeviceFingerprint.value.trim()
  if (!value) return null
  if (!/^[A-Za-z0-9+/=\-]+$/.test(value)) return false
  const parts = value.split('-')
  if (parts.length !== 4) return false
  return parts.every(part => part.length === 4)
})

const clearActivateError = () => {
  activateErrorMsg.value = ''
  activateShowSuccess.value = true
}

const resetActivationState = () => {
  activateCodeResult.value = ''
  activateDeviceFingerprint.value = ''
  activateErrorMsg.value = ''
  activateShowSuccess.value = true
}

const getOrderList = async () => {
  loading.value = true
  try {
    const params = {
      page: currentPage.value,
      size: pageSize.value
    }
    const response = await request.get('/order/all', { params })
    tableData.value = response.data.list
    total.value = response.data.total
  } catch (error) {
    console.error(error)
  } finally {
    loading.value = false
  }
}

const clearSearch = () => {
  searchForm.keyword = ''
  handleSearch()
}

const handleSearch = async () => {
  if (!searchForm.keyword) {
    resetSearch()
    return
  }
  loading.value = true
  try {
    const params = {
      keyword: searchForm.keyword,
      page: currentPage.value,
      size: pageSize.value
    }
    const response = await request.get('/order/search', { params })
    tableData.value = response.data.list
    total.value = response.data.total
    if (tableData.value.length === 0) {
      ElMessage.info('未找到相关订单')
    } else {
      ElMessage.success(`找到 ${tableData.value.length} 条订单`)
    }
  } catch (error) {
    console.error(error)
  } finally {
    loading.value = false
  }
}

const resetSearch = () => {
  searchForm.keyword = ''
  currentPage.value = 1
  getOrderList()
}

const handleCurrentChange = (newPage) => {
  currentPage.value = newPage
  if (searchForm.keyword) {
    handleSearch()
  } else {
    getOrderList()
  }
}

const formatStatus = (status) => {
  const statusMap = {
    0: '未支付',
    1: '完成',
    2: '已退款',
    3: '已关闭'
  }
  return statusMap[status] || '未知'
}

const getStatusTagType = (status) => {
  const typeMap = {
    0: 'warning',
    1: 'success',
    2: 'info',
    3: 'danger'
  }
  return typeMap[status] || 'info'
}

const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return '-'
  const date = new Date(dateTimeStr)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const openActivateDialog = (row) => {
  currentActivateRow.value = row
  showActivateDialog.value = true
  activateDeviceFingerprint.value = ''
  activateCodeResult.value = ''
  activateErrorMsg.value = ''
  activateShowSuccess.value = true
}

const confirmActivate = async () => {
  const fingerprint = activateDeviceFingerprint.value.trim()
  if (!fingerprint) {
    activateErrorMsg.value = '请输入注册码'
    return
  }
  if (!isActivateFingerprintValid.value) {
    activateErrorMsg.value = '注册码格式不正确，应为4组4字符组合（如 0002-N6R3-8896-NKPS）'
    return
  }
  activating.value = true
  activateErrorMsg.value = ''
  activateCodeResult.value = ''
  try {
    const res = await request.post('/aip-plugin/activate', {
      orderId: currentActivateRow.value.id,
      deviceFingerprint: fingerprint
    })
    if (res.code === '200') {
      activateCodeResult.value = res.data
      ElMessage.success('激活码生成成功')
      getOrderList()
    } else {
      activateErrorMsg.value = res.message || '生成失败'
      activateShowSuccess.value = false
    }
  } catch (err) {
    const msg = err?.message || err?.response?.data?.message || '生成失败，请稍后重试'
    activateErrorMsg.value = msg
    activateShowSuccess.value = false
  } finally {
    activating.value = false
  }
}

const copyActivateCode = () => {
  navigator.clipboard.writeText(activateCodeResult.value)
  ElMessage.success('激活码已复制到剪贴板')
}

const openViewCodeDialog = (row) => {
  viewCode.value = row.activationCode || '暂无激活码'
  showViewCodeDialog.value = true
}

const copyViewCode = () => {
  navigator.clipboard.writeText(viewCode.value)
  ElMessage.success('激活码已复制到剪贴板')
}

onMounted(() => {
  getOrderList()
})

</script>

<style scoped>
/* ===== 根容器 ===== */
.user-orders-container {
  --orders-font: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  padding: 10px 6px;
  font-family: var(--orders-font);
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 700px;
  width: 100%;
  box-sizing: border-box;
}

/* ===== 搜索区域 - 简洁透明版 ===== */
.search-area {
  margin-bottom: 18px;
  padding: 4px 0;
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
  transition: none;
  flex-shrink: 0;
}

.search-area::before {
  display: none;
}

.search-area:hover {
  box-shadow: none;
  transform: none;
}

/* 输入框样式保留但精简 */
:deep(.search-area .el-input__wrapper) {
  border-radius: 8px;
  box-shadow: 0 0 0 1px #cbd5e1 inset;
  padding: 4px 12px;
  transition: all 0.2s ease;
}

:deep(.search-area .el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px #94a3b8 inset;
}

:deep(.search-area .el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px #2f5bff inset, 0 0 0 3px rgba(47, 91, 255, 0.15) !important;
}

:deep(.search-area .el-input__inner) {
  font-family: var(--orders-font);
  color: #0f172a;
}

:deep(.search-area .el-input__prefix-inner .el-icon) {
  color: #64748b;
}

/* 搜索按钮 - 简洁版 */
:deep(.search-area .el-button--primary) {
  background: #2f5bff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(47, 91, 255, 0.25);
  transition: all 0.25s ease;
}

:deep(.search-area .el-button--primary:hover) {
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(47, 91, 255, 0.35);
  background: #1f43cc;
}

:deep(.search-area .el-button--primary:active) {
  transform: translateY(0);
}

/* ===== 表格滚动容器 ===== */
.table-wrapper {
  min-height: 0;
  overflow-x: auto;
  overflow-y: auto;
  border-radius: 12px;
  border: 1px solid #e6eaf2;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  -webkit-overflow-scrolling: touch;
  position: relative;
}

/* 滚动条样式 - 横向 + 纵向 */
.table-wrapper::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.table-wrapper::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}
.table-wrapper::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.table-wrapper::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* 表格本身 - 使用 min-width 触发横向滚动，不设 width:100% */
.table-wrapper .order-table {
  border: none !important;
  box-shadow: none !important;
  border-radius: 0 !important;
}

/* ===== 表格样式 ===== */
:deep(.order-table) {
  border-radius: 0 !important;
  overflow: visible;
  border: none !important;
  box-shadow: none !important;
}

:deep(.order-table.el-table--border .el-table__inner-wrapper::after),
:deep(.order-table .el-table__border-left-patch) {
  display: none;
}

:deep(.order-table.el-table--border) {
  border: none !important;
}

:deep(.order-table .el-table__header) {
  font-family: var(--orders-font);
}

:deep(.order-table .el-table__header th) {
  font-weight: 600;
  color: #0f172a;
  background-color: #f7f9fc !important;
  font-size: 13px;
  border-bottom: 1px solid #e6eaf2 !important;
  border-right: none !important;
  padding: 12px 10px;
}

:deep(.order-table .el-table__header th .cell) {
  font-weight: 600;
  color: #1e293b;
}

:deep(.order-table .el-table__body td) {
  text-align: center;
  border-bottom: 1px solid #f1f5f9 !important;
  border-right: none !important;
  padding: 12px 10px;
  font-size: 13px;
  color: #334155;
}

:deep(.order-table .el-table__body tr:hover > td) {
  background-color: rgba(47, 91, 255, 0.04) !important;
}

:deep(.order-table .el-table__body tr.el-table__row--striped td) {
  background-color: #f7f9fc !important;
}

:deep(.order-table .el-table__body tr.el-table__row--striped:hover > td) {
  background-color: rgba(47, 91, 255, 0.04) !important;
}

:deep(.order-table .el-table__body tr:last-child td) {
  border-bottom: none !important;
}

:deep(.order-table .el-table__empty-block) {
  min-height: 180px;
}

:deep(.order-table .el-table__empty-text) {
  color: #94a3b8;
  font-size: 14px;
}

/* ===== 金额 ===== */
.order-amount {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700 !important;
  font-size: 14px;
}

/* ===== 状态标签 ===== */
:deep(.status-tag.el-tag) {
  border-radius: 999px;
  border: none;
  font-weight: 600;
  padding: 0 12px;
  height: 24px;
  line-height: 22px;
  font-size: 12px;
}

:deep(.status-tag.el-tag--warning) {
  background: #fef3c7;
  color: #b45309;
}
:deep(.status-tag.el-tag--success) {
  background: #dcfce7;
  color: #15803d;
}
:deep(.status-tag.el-tag--info) {
  background: #dce6ff;
  color: #1f43cc;
}
:deep(.status-tag.el-tag--danger) {
  background: #fee2e2;
  color: #b91c1c;
}

/* ===== 操作按钮 ===== */
:deep(.action-btn.activate-btn) {
  background: #22c55e;
  border: none;
  color: #fff;
  font-weight: 600;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
  transition: all 0.25s ease;
}

:deep(.action-btn.activate-btn:hover) {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(34, 197, 94, 0.4);
  background: #16a34a;
}

:deep(.action-btn.view-btn) {
  background: #2f5bff;
  border: none;
  color: #fff;
  font-weight: 600;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(47, 91, 255, 0.3);
  transition: all 0.25s ease;
}

:deep(.action-btn.view-btn:hover) {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(47, 91, 255, 0.4);
  background: #1f43cc;
}

/* ===== 分页 ===== */
.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
}

:deep(.el-pagination .btn-prev),
:deep(.el-pagination .btn-next),
:deep(.el-pagination .el-pager li) {
  border-radius: 8px !important;
  min-width: 34px;
  height: 34px;
  line-height: 34px;
  margin: 0 3px;
  background: #ffffff;
  border: 1px solid #e6eaf2;
  color: #475569;
  font-weight: 500;
  transition: all 0.2s ease;
}

:deep(.el-pagination .el-pager li:hover),
:deep(.el-pagination .btn-prev:hover),
:deep(.el-pagination .btn-next:hover) {
  color: #2f5bff;
  border-color: #2f5bff;
}

:deep(.el-pagination .el-pager li.is-active) {
  background: #2f5bff !important;
  color: #ffffff !important;
  border-color: #2f5bff !important;
  font-weight: 600;
}

/* ===== 激活码弹窗（保持原有样式，仅保留必要部分） ===== */
/* 自定义头部容器 */
.dialog-header-custom {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 6px 8px 0;
  position: relative;
  z-index: 5;
}

/* 自定义关闭按钮（现代圆形毛玻璃 + 蓝橙 hover） */
.custom-close-btn {
  border: 1px solid rgba(226, 232, 240, 0.7);
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  font-size: 14px;
  font-weight: 700;
  color: #94a3b8;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  line-height: 24px;
  text-align: center;
  border-radius: 50%;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);
}

.custom-close-btn:hover {
  background: red;
  color: #ffffff;
  transform: rotate(90deg) scale(1.06);
  box-shadow: 0 6px 18px rgba(47, 91, 255, 0.32);
  border-color: transparent;
}

.custom-close-btn:active {
  transform: rotate(0deg) scale(0.92);
}

/* 内容区 */
.activation-content {
  padding: 0;
}

/* 标题 */
.activation-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 22px;
  font-size: 17px;
  font-weight: 800;
  background: #000;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.2px;
}

.activation-title .key-icon {
  flex-shrink: 0;
  color: #f59e0b;
  stroke: #f59e0b;
  filter: drop-shadow(0 2px 6px rgba(245, 158, 11, 0.3));
}

/* 表单输入组 */
.form-group {
  margin-bottom: 18px;
}

.input-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.input-label {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: 0.3px;
}

/* 现代输入框：等宽字体 + 蓝色光晕 focus */
.fingerprint-input :deep(.el-input) {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
  border-radius: 0 !important;
}

.fingerprint-input :deep(.el-input__wrapper) {
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
  padding: 0 !important;
  border-radius: 0 !important;
}

.fingerprint-input :deep(.el-input__inner) {
  border-radius: 12px;
  border: 1.5px solid rgba(226, 232, 240, 0.9);
  padding: 13px 16px;
  font-family: 'SF Mono', 'JetBrains Mono', Menlo, monospace;
  font-size: 14.5px;
  background: #fbfcfe;
  transition: all 0.25s ease;
  height: 48px !important;
  text-align: center;
  letter-spacing: 1.4px;
  color: #0f172a;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04) inset;
}

.fingerprint-input :deep(.el-input__inner::placeholder) {
  color: #cbd5e1;
  font-weight: 500;
  letter-spacing: 1.4px;
}

.fingerprint-input :deep(.el-input__inner:focus) {
  border-color: #2f5bff;
  box-shadow: 0 0 0 4px rgba(47, 91, 255, 0.14);
  background: #fff;
}

.format-hint {
  font-size: 12px;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
}

.format-hint.error { color: #dc2626; }
.format-hint.success { color: #22c55e; }

/* 有效期信息卡（蓝橙渐变背景 + 图标圆圈） */
.validity-row {
  display: flex;
  align-items: center;
  background:
      radial-gradient(ellipse 200px 100px at 0% 0%, rgba(47, 91, 255, 0.10), transparent 70%),
      radial-gradient(ellipse 200px 100px at 100% 100%, rgba(245, 158, 11, 0.10), transparent 70%),
      linear-gradient(135deg, #f7f9fc 0%, #fff7e6 100%);
  padding: 16px 18px;
  border-radius: 14px;
  margin-bottom: 20px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  position: relative;
  overflow: hidden;
}

.validity-row::after {
  content: '';
  position: absolute;
  bottom: 0; left: 10%; right: 10%;
  height: 1px;
  background: linear-gradient(90deg, transparent, #2f5bff, #f59e0b, transparent);
  opacity: 0.5;
}

.validity-item {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.validity-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(47, 91, 255, 0.16), rgba(245, 158, 11, 0.12));
  color: #2f5bff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 10px rgba(47, 91, 255, 0.14);
}

.validity-item:nth-child(3) .validity-icon {
  color: #f59e0b;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.16), rgba(251, 191, 36, 0.10));
  box-shadow: 0 4px 10px rgba(245, 158, 11, 0.14);
}

.validity-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.validity-item .label {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.validity-item .value {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  font-family: 'SF Mono', 'JetBrains Mono', Menlo, monospace;
}

.validity-divider {
  width: 1px;
  height: 30px;
  background: linear-gradient(180deg, transparent, rgba(47, 91, 255, 0.3), transparent);
  margin: 0 16px;
  flex-shrink: 0;
}

/* 生成按钮：蓝色渐变 + 光晕 */
.generate-btn {
  width: 100%;
  height: 46px;
  font-weight: 700;
  border-radius: 12px;
  background: linear-gradient(135deg, #2f5bff 0%, #5b7cff 100%);
  border: none;
  font-size: 14.5px;
  letter-spacing: 0.5px;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  color: #fff;
  box-shadow:
      0 8px 20px rgba(47, 91, 255, 0.32),
      0 1px 0 rgba(255, 255, 255, 0.2) inset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.generate-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow:
      0 14px 28px rgba(47, 91, 255, 0.42),
      0 1px 0 rgba(255, 255, 255, 0.2) inset;
  background: linear-gradient(135deg, #1f43cc 0%, #2f5bff 100%);
}

.generate-btn:active:not(:disabled) {
  transform: translateY(0);
}

.generate-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
  background: #cbd5e1;
}

/* 错误提示 */
.license-error-alert {
  margin-top: 14px;
  border-radius: 12px;
  border: 1px solid rgba(220, 38, 38, 0.2);
}

/* 激活码结果视图（生成成功） */
.activation-result-full {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 14px 10px 4px;
  text-align: center;
  animation: resultFadeIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
}

@keyframes resultFadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}

.activation-result-full .result-header {

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 18px;
}

.activation-result-full .result-icon {
  font-size: 22px;
  line-height: 1;
  filter: drop-shadow(0 4px 10px rgba(34, 197, 94, 0.35));
}

/* 标题默认样式（渐变） */
.activation-result-full .result-title {
  font-size: 20px;
  font-weight: 800;
  background: linear-gradient(135deg, #1e293b 0%, #2f5bff 60%, #f59e0b 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
}

/* 标题纯黑色样式（生成成功后使用） */
.activation-result-full .result-title.title-dark {
  background: none !important;
  -webkit-background-clip: unset !important;
  background-clip: unset !important;
  -webkit-text-fill-color: #1e293b !important;
  color: #1e293b !important;
}

/* 激活码显示区（现代卡片） - 默认带自定义滚动条 */
.activation-result-full .code-display {
  width: 100%;
  padding: 16px 18px;
  background:
      radial-gradient(ellipse 200px 100px at 0% 0%, rgba(47, 91, 255, 0.06), transparent 70%),
      #ffffff;
  border-radius: 6px;
  color: #0f172a;
  font-family: 'SF Mono', 'JetBrains Mono', Menlo, monospace;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  word-break: break-all;
  margin-bottom: 16px;
  line-height: 1.7;
  max-height: 180px;
  overflow-y: auto;
  position: relative;
}


/* 自定义滚动条样式（默认状态下） */
.activation-result-full .code-display::-webkit-scrollbar {
  width: 4px;
}
.activation-result-full .code-display::-webkit-scrollbar-thumb {
  background: #a4b4c1;
  border-radius: 4px;
}

/* 激活码显示区（生成成功后）- 重置为默认滚动条 */
.activation-result-full .code-display.default-scroll::-webkit-scrollbar {
  width: unset;
  height: unset;
}
.activation-result-full .code-display.default-scroll::-webkit-scrollbar-track {
  background: unset;
  border-radius: unset;
}
.activation-result-full .code-display.default-scroll::-webkit-scrollbar-thumb {
  background: unset;
  border-radius: unset;
}
.activation-result-full .code-display.default-scroll::-webkit-scrollbar-thumb:hover {
  background: unset;
}

/* 复制按钮（蓝橙渐变） */
.activation-result-full .copy-btn {
  width: 100%;
  height: 46px;
  border-radius: 12px;
  background: linear-gradient(135deg, #2f5bff 0%, #f59e0b 100%);
  border: none;
  color: #ffffff;
  font-weight: 700;
  font-size: 15px;
  letter-spacing: 0.4px;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow:
      0 8px 20px rgba(47, 91, 255, 0.28),
      0 4px 12px rgba(245, 158, 11, 0.18);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
}

.activation-result-full .copy-btn:hover {
  transform: translateY(-2px);
  box-shadow:
      0 14px 28px rgba(47, 91, 255, 0.36),
      0 6px 16px rgba(245, 158, 11, 0.24);
  filter: brightness(1.05);
}

.activation-result-full .copy-btn:active {
  transform: translateY(0);
  box-shadow: 0 4px 12px rgba(47, 91, 255, 0.22);
}

.activation-result-full .hint {
  font-size: 13px;
  color: #64748b;
  margin-top: 12px;
  margin-bottom: 0;
  font-weight: 500;
}

/* 底部信息 */
.footer-info {
  margin-top: 18px;
  text-align: center;
  font-size: 11.5px;
  color: #94a3b8;
  letter-spacing: 0.4px;
  font-weight: 500;
}

/* ===== 响应式 ===== */
@media (max-width: 768px) {
  .user-orders-container {
    padding: 12px 14px;
  }
  .search-area {
    padding: 2px 0;
  }
  :deep(.search-area .el-input) {
    width: 200px !important;
  }
  .activation-dialog :deep(.el-dialog) {
    width: 92% !important;
  }

  .activation-dialog :deep(.el-dialog__body) {
    padding: 18px 20px 20px;
  }

  .validity-row {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 14px;
  }

  .validity-divider {
    width: 100%;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(47, 91, 255, 0.3), transparent);
    margin: 0;
  }
}

</style>

<style>
/* ===== 激活码弹窗全局样式 ===== */
/* 使用更具体的选择器，提高权重 */
.el-dialog.activation-dialog {
  border-radius: 20px !important;
  overflow: hidden !important;
  background:
      radial-gradient(ellipse at 50% 0%, rgba(91, 141, 239, 0.12) 0%, transparent 70%),
      linear-gradient(180deg, #bed9f7 0%, #eaf3fc 80%);
  box-shadow:
      0 0 0 1px rgba(15, 23, 42, 0.04),
      0 24px 56px rgba(15, 23, 42, 0.18),
      0 8px 24px rgba(47, 91, 255, 0.12) !important;
  border: none !important;
}

.el-dialog.activation-dialog .el-dialog__header {
  padding: 0 !important;
  margin: 0 !important;
  border: none !important;
  position: relative !important;
}

.el-dialog.activation-dialog .el-dialog__headerbtn {
  display: none !important;
}

.el-dialog.activation-dialog .el-dialog__body {
  padding: 22px 26px 24px !important;
  background: transparent !important;
}

/* 查看激活码弹窗 - 同样背景色 */
.el-dialog.view-code-dialog {
  border-radius: 20px !important;
  overflow: hidden !important;
  background:
      radial-gradient(ellipse at 50% 0%, rgba(91, 141, 239, 0.12) 0%, transparent 70%),
      linear-gradient(180deg, #bed9f7 0%, #eaf3fc 80%);
  box-shadow:
      0 0 0 1px rgba(15, 23, 42, 0.04),
      0 24px 56px rgba(15, 23, 42, 0.18),
      0 8px 24px rgba(47, 91, 255, 0.12) !important;
  border: none !important;
}

.el-dialog.view-code-dialog .el-dialog__headerbtn {
  display: none !important;
}

.el-dialog.view-code-dialog .el-dialog__body {
  padding: 10px 26px 24px !important;
  background: transparent !important;
}

.el-dialog.view-code-dialog .activation-result-full .code-display {
  background:
      radial-gradient(ellipse 200px 100px at 0% 0%, rgba(47, 91, 255, 0.06), transparent 70%),
      #f7f9fc !important;
  border: 1px solid rgba(226, 232, 240, 0.9) !important;
}

/* 遮罩层背景模糊 */
.activation-dialog-overlay {
  backdrop-filter: blur(6px) !important;
  -webkit-backdrop-filter: blur(6px) !important;
  background-color: rgba(0, 0, 0, 0.35) !important;
}

/* 查看激活码：干净、专业的标准弹窗 */
.el-dialog.professional-code-dialog {
  border-radius: 12px;
}

.el-dialog.professional-code-dialog .el-dialog__header {
  padding: 20px 24px 0;
  margin-right: 0;
}

.el-dialog.professional-code-dialog .el-dialog__title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
}

.el-dialog.professional-code-dialog .el-dialog__body {
  padding: 18px 24px 24px;
}

.code-dialog-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.code-dialog-label {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.code-dialog-value {
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f7f9fc;
  color: #0f172a;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
  font-size: 13px;
  line-height: 1.65;
  word-break: break-all;
  max-height: 220px;
  overflow-y: auto;
}

.code-dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}
</style>